<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import { useForm } from '@inertiajs/vue3'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { InputNumber, Select } from 'primevue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faBoxOpen, faPrint } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faBoxOpen, faPrint)

const props = defineProps<{
    title?: string
    pageHead?: PageHeadingTS
    seng_products: {
        id: number
        name: string
        code: string
        price: number
        is_pinned: boolean
    }[]
    customers: {
        id: number
        name: string
    }[]
    flexo_product: {
        id: number
        name: string
        price: number
    } | null
    flexo_customer_prices: Record<number, number>
    default_product_id?: number | null
}>()

const selectedType = ref<'seng' | 'flexo'>('seng')

const form = useForm({
    type: 'seng' as 'seng' | 'flexo',
    reference: '',
    customer_id: null as number | null,
    product_id: props.default_product_id ?? null as number | null,
    quantity: 1,
    dimension_width: null as number | null,
    dimension_height: null as number | null,
    note: '',
})

watch(selectedType, (val) => {
    form.type = val
    form.errors.type = ''
})

const flexoUnitPrice = computed(() => {
    if (!props.flexo_product) return 0
    if (form.customer_id && props.flexo_customer_prices[form.customer_id]) {
        return props.flexo_customer_prices[form.customer_id]
    }
    return props.flexo_product.price
})

const flexoTotal = computed(() => {
    if (!form.dimension_width || !form.dimension_height || !form.quantity) return 0
    return form.quantity * form.dimension_width * form.dimension_height * flexoUnitPrice.value
})

const sengTotal = computed(() => {
    if (!form.product_id) return 0
    const product = props.seng_products.find(p => p.id === form.product_id)
    if (!product) return 0
    return form.quantity * product.price
})

const selectedSengProduct = computed(() =>
    props.seng_products.find(p => p.id === form.product_id) ?? null
)

const onSubmit = () => {
    form.post(route('casheer.orders.store'))
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="px-4 sm:px-6 lg:px-8 mt-6 max-w-2xl">

        <!-- Type Selector -->
        <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-2">Jenis Produk <span class="text-red-500">*</span></label>
            <div class="grid grid-cols-2 gap-3">
                <button
                    type="button"
                    @click="selectedType = 'seng'"
                    class="flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 font-semibold transition"
                    :class="selectedType === 'seng'
                        ? 'border-amber-400 bg-amber-50 text-amber-700'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-amber-300'"
                >
                    <FontAwesomeIcon icon="fal fa-box-open" fixed-width aria-hidden="true" />
                    Seng
                </button>
                <button
                    type="button"
                    @click="selectedType = 'flexo'"
                    class="flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 font-semibold transition"
                    :class="selectedType === 'flexo'
                        ? 'border-blue-400 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-blue-300'"
                >
                    <FontAwesomeIcon icon="fal fa-print" fixed-width aria-hidden="true" />
                    Flexo
                </button>
            </div>
            <p v-if="form.errors.type" class="mt-1 text-sm text-red-500">{{ form.errors.type }}</p>
        </div>

        <div class="space-y-5">

            <!-- No. Order -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nomor Order</label>
                <input
                    v-model="form.reference"
                    type="text"
                    placeholder="Otomatis dibuat jika kosong"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p v-if="form.errors.reference" class="mt-1 text-sm text-red-500">{{ form.errors.reference }}</p>
            </div>

            <!-- Pelanggan -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Pelanggan</label>
                <Select
                    v-model="form.customer_id"
                    :options="customers"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Pilih pelanggan..."
                    showClear
                    filter
                    fluid
                    :invalid="!!form.errors.customer_id"
                />
                <p v-if="form.errors.customer_id" class="mt-1 text-sm text-red-500">{{ form.errors.customer_id }}</p>
            </div>

            <!-- SENG: Pilih produk -->
            <template v-if="selectedType === 'seng'">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Produk Seng <span class="text-red-500">*</span></label>
                    <Select
                        v-model="form.product_id"
                        :options="seng_products"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Pilih produk Seng..."
                        filter
                        fluid
                        :invalid="!!form.errors.product_id"
                    >
                        <template #option="{ option }">
                            <div class="flex justify-between w-full gap-4">
                                <span>{{ option.name }}</span>
                                <span class="text-gray-400 text-sm">{{ useFormatCurrency(option.price) }}</span>
                            </div>
                        </template>
                    </Select>
                    <p v-if="form.errors.product_id" class="mt-1 text-sm text-red-500">{{ form.errors.product_id }}</p>
                </div>
            </template>

            <!-- FLEXO: Dimensi -->
            <template v-if="selectedType === 'flexo'">
                <div v-if="!flexo_product" class="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-700">
                    Produk Flexo belum tersedia. Tambahkan terlebih dahulu di halaman Produk.
                </div>
                <template v-else>
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Panjang (cm) <span class="text-red-500">*</span></label>
                            <InputNumber
                                v-model="form.dimension_height"
                                placeholder="cm"
                                :minFractionDigits="0"
                                :maxFractionDigits="2"
                                :min="0.1"
                                fluid
                                :invalid="!!form.errors.dimension_height"
                            />
                            <p v-if="form.errors.dimension_height" class="mt-1 text-sm text-red-500">{{ form.errors.dimension_height }}</p>
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Lebar (cm) <span class="text-red-500">*</span></label>
                            <InputNumber
                                v-model="form.dimension_width"
                                placeholder="cm"
                                :minFractionDigits="0"
                                :maxFractionDigits="2"
                                :min="0.1"
                                fluid
                                :invalid="!!form.errors.dimension_width"
                            />
                            <p v-if="form.errors.dimension_width" class="mt-1 text-sm text-red-500">{{ form.errors.dimension_width }}</p>
                        </div>
                    </div>

                    <!-- Harga per unit info -->
                    <div class="text-sm text-gray-500">
                        Harga per unit:
                        <span class="font-semibold text-gray-700">{{ useFormatCurrency(flexoUnitPrice) }}/cm²</span>
                        <span v-if="form.customer_id && flexo_customer_prices[form.customer_id]" class="ml-1 text-blue-600 text-xs">(harga khusus)</span>
                    </div>
                </template>
            </template>

            <!-- Jumlah -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Jumlah <span class="text-red-500">*</span></label>
                <InputNumber
                    v-model="form.quantity"
                    :min="1"
                    :max="999"
                    showButtons
                    fluid
                    :invalid="!!form.errors.quantity"
                />
                <p v-if="form.errors.quantity" class="mt-1 text-sm text-red-500">{{ form.errors.quantity }}</p>
            </div>

            <!-- Catatan -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
                <textarea
                    v-model="form.note"
                    rows="3"
                    placeholder="Catatan tambahan..."
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p v-if="form.errors.note" class="mt-1 text-sm text-red-500">{{ form.errors.note }}</p>
            </div>

            <!-- Preview total -->
            <div class="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
                <div v-if="selectedType === 'flexo' && flexo_product" class="text-sm text-gray-500 mb-1">
                    {{ form.quantity }} pcs × {{ form.dimension_height || '?' }} cm × {{ form.dimension_width || '?' }} cm × {{ useFormatCurrency(flexoUnitPrice) }}
                </div>
                <div v-else-if="selectedType === 'seng' && selectedSengProduct" class="text-sm text-gray-500 mb-1">
                    {{ form.quantity }} pcs × {{ useFormatCurrency(selectedSengProduct.price) }}
                </div>
                <div class="text-lg font-bold text-gray-800">
                    Total: {{ useFormatCurrency(selectedType === 'flexo' ? flexoTotal : sengTotal) }}
                </div>
            </div>

            <!-- Submit -->
            <Button
                label="Buat Order"
                type="save"
                full
                :loading="form.processing"
                :disabled="form.processing || (selectedType === 'flexo' && !flexo_product)"
                @click="onSubmit"
            />
        </div>
    </div>
</template>
